import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsDefaultMaterial,
  HsGraphicCollection,
  HsGraphicObject,
  HsGraphicPoints,
  HsLightStorage,
  HsMatrix4,
  HsPointsMaterial,
  HsRegularGeometryData,
  HsShaderData,
  HsShaderObjectType,
  hsGenerateCubeGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  type HsDefaultShader,
  type HsGraphics,
  type HsHypersceneViewer,
  type HsIndexedGeometry,
  type HsMaterial,
  type HsRegularGeometry,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makePinkPointLights } from '../../functions/make-pink-point-lights.function';

const GRID_SIZE: number = 30;
const GRID_STEP: number = 2;
const POINT_CLOUD_SIDE: number = 5;
const POINT_CLOUD_HEIGHT: number = 50;
const POINT_SIZE: number = 4;

function makeLitMaterial(
  gl: WebGL2RenderingContext,
  objectType: HsShaderObjectType,
  lights: HsLightStorage,
  color: readonly [number, number, number]
): HsMaterial {
  const shaderData: HsShaderData = new HsShaderData(objectType);
  shaderData.setLightStorage(lights).addColor().addDepth().addNormals().addDiffuseLighting();
  const material: HsMaterial = new HsDefaultMaterial(gl, shaderData.makeShader());
  material.color.set(color[0], color[1], color[2]);
  return material;
}

/** Points of a unit sphere sampled on a `side`^3 grid, colored by grid position. */
function makeSpherePoints(side: number): { positions: number[]; colors: number[] } {
  const positions: number[] = [];
  const colors: number[] = [];
  for (let i: number = 0; i < side; i++) {
    for (let j: number = 0; j < side; j++) {
      for (let k: number = 0; k < side; k++) {
        const x: number = (i / side) * 2 - 1;
        const y: number = (j / side) * 2 - 1;
        const z: number = (k / side) * 2 - 1;
        if (x * x + y * y + z * z - 1 < 0) {
          // (z, x, y): a third of a turn around (1, 1, 1), as the legacy point cloud factory oriented clouds.
          positions.push(z, x, y);
          colors.push(i / side, j / side, k / side);
        }
      }
    }
  }
  return { positions, colors };
}

/**
 * Many objects of different kinds in one scene: a 30 x 30 x 30 checkered block of cubes, a block of small point
 * clouds above it, and two single cubes built from non-indexed and indexed geometry.
 */
@Component({
  selector: 'unique-objects',
  templateUrl: './unique-objects.component.html',
  styleUrls: ['./unique-objects.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class UniqueObjectsDemo extends DemoViewerPage {
  protected initScene(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const gl: WebGL2RenderingContext = viewer.gl;
    const lights: HsLightStorage = makePinkPointLights();
    const geometry: HsRegularGeometry | null = hsMakeGeometry(graphics, hsGenerateCubeGeometryData());
    const indexedGeometry: HsIndexedGeometry | null = hsMakeIndexedGeometry(
      graphics,
      hsIndexGeometry(hsGenerateCubeGeometryData())
    );
    if (geometry === null || indexedGeometry === null) {
      return;
    }

    const standardObject: HsGraphicObject = new HsGraphicObject(
      getUuid(),
      'standardCube',
      geometry,
      makeLitMaterial(gl, HsShaderObjectType.single, lights, [1, 1, 1])
    );
    standardObject.worldMatrix.compose([-10, 2, 0], [0, 0, 0, 1], [2, 2, 2]);
    standardObject.updateBoundingSphere();
    viewer.add(standardObject);

    const indexedObject: HsGraphicObject = new HsGraphicObject(
      getUuid(),
      'indexedCube',
      indexedGeometry,
      makeLitMaterial(gl, HsShaderObjectType.single, lights, [1, 0.75, 0])
    );
    indexedObject.worldMatrix.compose([-15, 2, 0], [0, 0, 0, 1], [2, 2, 2]);
    indexedObject.updateBoundingSphere();
    viewer.add(indexedObject);

    this.addGround(viewer, indexedGeometry, makeLitMaterial(gl, HsShaderObjectType.collection, lights, [1, 1, 1]));
    this.addPointClouds(viewer);

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition([2, 10, 30]);
    viewer.camera.updateProjectionMatrix();
  }

  /** The checkered block, drawn as one instanced collection with a color per cube. */
  private addGround(viewer: HsHypersceneViewer, geometry: HsIndexedGeometry, material: HsMaterial): void {
    const lightColor: readonly number[] = [1, 1, 1];
    const darkColor: readonly number[] = [0.3, 0.3, 0.3];
    const collection: HsGraphicCollection = new HsGraphicCollection(getUuid(), 'ground', geometry, material);
    const matrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
    for (let i: number = 0; i < GRID_SIZE; i++) {
      for (let j: number = 0; j < GRID_SIZE; j++) {
        for (let k: number = 0; k < GRID_SIZE; k++) {
          const cellIsDark: boolean = (i + j + k) % 2 === 1;
          matrix.compose(
            [i * GRID_STEP - GRID_SIZE, k * GRID_STEP - GRID_SIZE, j * GRID_STEP - GRID_SIZE],
            [0, 0, 0, 1],
            [0.999, 0.999, 0.999]
          );
          collection.addInstance(matrix.values, cellIsDark ? darkColor : lightColor);
        }
      }
    }
    collection.updateCenter();
    collection.updateBoundingSphere();
    collection.updateBuffers(viewer.surface.graphics);
    viewer.add(collection);
  }

  /** One small sphere point cloud above every ground cube, merged into a single point object. */
  private addPointClouds(viewer: HsHypersceneViewer): void {
    const cloud: { positions: number[]; colors: number[] } = makeSpherePoints(POINT_CLOUD_SIDE);
    const positions: number[] = [];
    const colors: number[] = [];
    for (let i: number = 0; i < GRID_SIZE; i++) {
      for (let j: number = 0; j < GRID_SIZE; j++) {
        for (let k: number = 0; k < GRID_SIZE; k++) {
          const x: number = i * GRID_STEP - GRID_SIZE;
          const y: number = k * GRID_STEP - GRID_SIZE + POINT_CLOUD_HEIGHT;
          const z: number = j * GRID_STEP - GRID_SIZE;
          for (let p: number = 0; p < cloud.positions.length; p += 3) {
            positions.push(cloud.positions[p] + x, cloud.positions[p + 1] + y, cloud.positions[p + 2] + z);
          }
          colors.push(...cloud.colors);
        }
      }
    }
    // Point geometry keeps the per-point colors where meshes keep normals.
    const geometry: HsRegularGeometry | null = hsMakeGeometry(
      viewer.surface.graphics,
      new HsRegularGeometryData(positions, colors)
    );
    if (geometry === null) {
      return;
    }
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.addColor().addDepth().addPoints();
    const material: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial(viewer.gl, shaderData.makeShader());
    const points: HsGraphicPoints = new HsGraphicPoints(getUuid(), 'pointClouds', geometry, material);
    points.setPointsSize(viewer.gl, POINT_SIZE);
    points.updateBoundingSphere();
    viewer.add(points);
  }
}
